import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Calculator, FileText, Users, Briefcase } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { SERVICES } from "@/constants/testIds/site";

const ICONS = [Calculator, FileText, Users, Briefcase];

export default function Services() {
  const { t } = useLanguage();

  return (
    <section
      id="services"
      data-testid={SERVICES.section}
      className="py-20 md:py-32 border-t border-[#1A1A1A]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-24">
          <div className="md:col-span-4">
            <p className="font-sans uppercase tracking-[0.28em] text-xs text-[#8B1A1A]">
              {t.services.eyebrow}
            </p>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif font-light text-[#1A1A1A] text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t.services.title}
            </h2>
            <p className="mt-6 text-[#5A5A5A] text-base md:text-lg max-w-2xl leading-relaxed">
              {t.services.lede}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#1A1A1A]/10">
          {t.services.items.map((item, i) => {
            const Icon = ICONS[i] || Calculator;
            return (
              <motion.article
                key={item.key}
                data-testid={SERVICES.card(item.key)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="group relative p-8 md:p-10 border-r border-b border-[#1A1A1A]/10 hover:bg-white transition-colors duration-500"
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-serif text-sm text-[#8B1A1A] tracking-widest">
                    {item.key}
                  </span>
                  <Icon
                    strokeWidth={1.25}
                    className="h-8 w-8 text-[#1A1A1A] group-hover:text-[#8B1A1A] transition-colors duration-500"
                  />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#1A1A1A] leading-tight mb-4">
                  {item.title}
                </h3>
                <p className="text-[#5A5A5A] text-sm md:text-base leading-relaxed">
                  {item.body}
                </p>
                <div className="mt-10 h-px w-8 bg-[#1A1A1A]/20 group-hover:w-16 group-hover:bg-[#8B1A1A] transition-all duration-500" />
                <ArrowUpRight
                  className="absolute bottom-8 right-8 h-4 w-4 text-[#1A1A1A]/20 group-hover:text-[#8B1A1A] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
                  strokeWidth={1.5}
                />
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
